import { PARTY_INVITE_TTL_MS, type LobbyServerEvent } from '@danda/shared';
import { notificationService } from '@/lib/notifications';
import type { EventHandlerContext } from './lobby-event-types';

export function handleInviteEvents(event: LobbyServerEvent, ctx: EventHandlerContext): boolean {
  const { get } = ctx;
  const state = get();

  if (event.type === 'lobby:party-invite-removed') {
    // Server removed the invite (declined via Tavian, or expired): prune the
    // banner so a stale Accept can't hit "Invite not found or has expired".
    state.removePendingInvite(event.inviteId);
    return true;
  }

  if (event.type !== 'lobby:party-invite-received') return false;

  // The server now re-delivers every pending invite on every (re)connect
  // (closing the gap where a missed/dropped push had no recovery path), AND
  // lobby:session-state clears pendingInvites first so the replay can
  // reconcile stale ones (see session-events.ts). That means pendingInvites
  // is EMPTY right before this handler runs on a normal reconnect, even for
  // an invite the user has already seen, so dedupe against `notifications`
  // instead, which is NOT cleared on reconnect. Without this, the
  // pendingInvites-only check silently no-ops (the array it checks was just
  // wiped) and every reconnect inside the invite's TTL fires another
  // "Party Invite" toast + OS notification for the same invite. That TTL is
  // now days rather than minutes, so the dedupe carries a lot more weight.
  const alreadyNotified =
    !!event.inviteId &&
    state.notifications.some(
      (n) => n.type === 'party_invite' && n.data?.inviteId === event.inviteId
    );

  const selectedChar = state.characters.find((c) => c.id === state.selectedCharacterId);
  // Still guard the actionable list itself in case a caller re-delivers
  // within the same tick before session-state's clear has run (defensive;
  // the reconciliation ordering above shouldn't allow it in practice).
  if (!event.inviteId || !state.pendingInvites.some((i) => i.id === event.inviteId)) {
    state.addPendingInvite({
      // Real server-issued invite id: Tavian accept/decline requires it.
      id: event.inviteId ?? `invite-${Date.now()}`,
      characterId: state.selectedCharacterId ?? '',
      characterName: selectedChar?.name ?? 'Unknown',
      characterType: 'pc',
      invitedBy: event.invitedBy,
      createdAt: event.sent_at,
      // Local approximation only. The server stamps the authoritative
      // `expiresAt` and sweeps on it; this push carries no expiry field, so
      // we rebuild it from the shared TTL rather than a magic number.
      expiresAt: new Date(Date.now() + PARTY_INVITE_TTL_MS).toISOString(),
    });
  }

  if (alreadyNotified) {
    return true;
  }

  state.addNotification({
    id: `notif-${Date.now()}`,
    type: 'party_invite',
    title: 'Party Invite',
    message: `You've been invited to join ${event.partyName}`,
    timestamp: new Date(event.sent_at),
    isRead: false,
    data: { partyId: event.partyId, partyName: event.partyName, inviteId: event.inviteId },
  });
  if (typeof window !== 'undefined' && !document.hasFocus()) {
    notificationService.notify('Party Invite', {
      body: `${event.invitedBy} invited you to join ${event.partyName}`,
      type: 'party_invite',
    });
  }

  return true;
}
